import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Avatar from './Avatar';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const hasRole = (roleName: string) =>
    user?.roles?.some((role) => (typeof role === 'string' ? role === roleName : role.name === roleName)) || false;
  const isHubStaff = hasRole('hub_leader') || hasRole('staff') || hasRole('admin');
  const isTrainer = hasRole('trainer');
  // Trainer-only accounts get just the Training area; planter data stays with hub staff.
  const isTrainerOnly = isTrainer && !isHubStaff && !hasRole('assessor') && !hasRole('coach') && !hasRole('assessment-admin');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const pipelineNav = isHubStaff ? [{ name: 'Pipeline', href: '/planters/pipeline' }] : [];
  const reportsNav = isHubStaff ? [{ name: 'Reports', href: '/reports' }] : [];

  // Build navigation based on user roles
  const trainingNav = isHubStaff || isTrainer ? [{ name: 'Training', href: '/training' }] : [];
  const navigation = isTrainerOnly
    ? trainingNav
    : [
      { name: 'Dashboard', href: '/dashboard' },
      ...pipelineNav,
      { name: 'Planters', href: '/planters' },
      ...reportsNav,
      { name: 'Assessment', href: '/assessments' },
      ...trainingNav,
    ];

  const isAssessmentRoute = location.pathname.startsWith('/assessments')
    || location.pathname.startsWith('/onboarding');
  const isPlantersRoute = location.pathname.startsWith('/planters')
    || location.pathname.startsWith('/map');

  const isActive = (href: string) => {
    if (href === '/assessments') return isAssessmentRoute;
    if (href === '/planters/pipeline') return location.pathname.startsWith('/planters/pipeline');
    if (href === '/planters') return isPlantersRoute && !location.pathname.startsWith('/planters/pipeline');
    return location.pathname.startsWith(href);
  };

  const secondaryLinks: { name: string; href: string }[] = isTrainerOnly ? [] : [
    { name: 'Map', href: '/map' },
    { name: 'Trainers', href: '/trainers' },
    { name: 'Coaches', href: '/coaches' },
    { name: 'Churches', href: '/church-directory' },
  ];

  const isSecondaryActive = (href: string) => location.pathname.startsWith(href);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 md:h-20">
            <div className="flex min-w-0 flex-1">
              <div className="flex-shrink-0 flex items-center">
                <img
                  className="h-8 w-auto sm:h-10 mr-2 sm:mr-3"
                  src="/logo.png"
                  alt="242Network"
                />
                <h1 className="text-base sm:text-xl font-bold text-primary-600 truncate md:hidden lg:block">242Network Hub</h1>
              </div>
              <div className="hidden md:ml-4 lg:ml-6 md:flex md:space-x-4 lg:space-x-5 xl:space-x-8">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive(item.href)
                        ? 'border-primary-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-primary-300 hover:text-gray-700'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
            <div className="hidden md:ml-6 md:flex md:items-center">
              <div className="ml-3 relative">
                <div className="flex items-center space-x-3">
                  <Avatar 
                    name={`${user?.firstName || ''} ${user?.lastName || ''}`}
                    size="sm"
                  />
                  <span className="text-sm text-gray-700 whitespace-nowrap hidden xl:inline">
                    {user?.firstName} {user?.lastName}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm leading-4 font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors min-h-touch"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
            <div className="md:hidden flex items-center space-x-2">
              <Avatar 
                name={`${user?.firstName || ''} ${user?.lastName || ''}`}
                size="sm"
              />
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                Logout
              </button>
            </div>
          </div>
          {secondaryLinks.length > 0 && <div className="flex flex-wrap gap-x-4 gap-y-1.5 py-2 border-t border-gray-100">
            {secondaryLinks.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-xs font-medium whitespace-nowrap ${
                  isSecondaryActive(item.href) ? 'text-primary-700' : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>}
        </div>
      </nav>

      {/* Main content */}
      <main className="py-6 sm:py-10 pb-24 md:pb-0">
        <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8">
          {children}
        </div>
      </main>

      {/* Mobile footer navigation */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(15,23,42,0.08)] pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-around px-4 py-2">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`flex flex-col items-center justify-center px-2 py-2 text-xs font-semibold ${
                isActive(item.href)
                  ? 'text-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {item.name}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Layout;
